import { Route, Routes } from 'react-router-dom'
import Tickets from 'routes/Tickets'
import NotFound from 'routes/NotFound'
import Page from 'components/global/Page'
import Accommodation from 'routes/Accommodation'
import OrderAndPayment from 'routes/OrderAndPayment'
import Upsell from 'routes/Upsell'
import AccommodationDetails from 'routes/AccommodationDetails'
import { useApp } from 'store/steps'
import { setCart } from 'store/cart-slice'
import { useCreateCartMutation, useGetCartQuery } from 'store/api-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useEffect } from 'react'
import FullPageLoader from 'components/global/FullPageLoader'
import ServerErrorModal from 'components/global/ServerErrorModal'
import { useTickets } from 'store/ticket-slice/hooks'
import { setIsAppAccessible, setIsErrorModalOpened } from 'store/general-slice'
import { pushDataLayer } from 'helpers/analytics'
import { creatorAccommodationFirstStep } from 'routes/basenames'
import { CART_TYPE_ACCOMMODATION } from 'constants/app'
import FamilyConflictModal from 'components/global/Family/components/FamilyConflictModal'
import {
    setFamilyPackageSelection,
    setIsPackageConflictModalOpened,
    setIsSeniorConflictModalOpen,
} from 'store/ticket-slice'
import SeniorConflictModal from 'components/global/Senior/SeniorConflictModal'
import { catchErrorByStatus } from 'helpers/errors'
import ConfirmationModal from 'components/global/ConfirmationModal'
import { STATUS_CODE_NO_SESSION } from 'constants/system'
import { DEFAULT_LANG } from 'constants/i18n'

const CreatorAccommodation = () => {
    const dispatch = useAppDispatch()
    const { getPath, language, appMode } = useApp()
    const { isTicketProcessing } = useTickets()

    const { cart } = useAppSelector((state) => state.cart)

    const {
        isFetching: isCartFetching,
        isError: isCartError,
        data: cartData,
        error: cartError,
    } = useGetCartQuery({
        _locale: language ?? DEFAULT_LANG,
        cartCreatorType: CART_TYPE_ACCOMMODATION,
    })
    const [createCart, createCartStatus] = useCreateCartMutation()

    const {
        isError: isCreateCartError,
        isSuccess: isCreateCartSuccess,
        data: createCartData,
    } = createCartStatus
    const isCartAlreadyCreated = cart?.cartId

    const handleAcceptError = () => {
        // reset session after showing modal with 'generic global error'
        createCart({
            _locale: language ?? DEFAULT_LANG,
            cartCreatorType: CART_TYPE_ACCOMMODATION,
        }).then(() => {
            dispatch(setIsErrorModalOpened(false))
            window.location.replace(creatorAccommodationFirstStep)
        })
    }

    const handleAcceptConflict = () => {
        dispatch(setFamilyPackageSelection({ isSelected: false }))
        dispatch(setIsSeniorConflictModalOpen(false))
        dispatch(setIsPackageConflictModalOpened(false))
    }

    useEffect(() => {
        if (isCartFetching || isCartAlreadyCreated) return

        if (isCartError && cartError) {
            catchErrorByStatus({
                error: cartError,
                ignored: [STATUS_CODE_NO_SESSION],
            }) ||
                createCart({
                    _locale: language ?? DEFAULT_LANG,
                    cartCreatorType: CART_TYPE_ACCOMMODATION,
                })
            return
        }

        if (cartData) dispatch(setCart({ ...cartData, appMode }))
    }, [isCartFetching])

    useEffect(() => {
        isCreateCartError &&
            dispatch(setIsAppAccessible({ isAppAccessible: false }))
    }, [isCreateCartError])

    useEffect(() => {
        if (!isCreateCartSuccess) return

        dispatch(
            setCart({
                cart: { cartId: createCartData?.cartId },
                appMode,
                isFetchingLocked: true,
            })
        )
    }, [isCreateCartSuccess])

    useEffect(() => {
        // < analytics
        const storageRedirectSource = sessionStorage.getItem('creatorSource')
        let redirectSource = 'direct'

        if (storageRedirectSource) {
            redirectSource = storageRedirectSource
            sessionStorage.removeItem('creatorSource')
        }

        pushDataLayer({
            event: 'funnelStep',
            event_category: 'accommodation',
            event_action: 'accomodation_order_start',
            event_label: redirectSource,
        })
        // analytics >
    }, [])

    return !isCartAlreadyCreated ? (
        <FullPageLoader />
    ) : (
        <Page>
            <Routes>
                <Route path="/" element={<Accommodation />} />
                <Route
                    path={getPath('accommodation_details')}
                    element={<AccommodationDetails />}
                />
                <Route path={getPath('tickets')} element={<Tickets />} />
                <Route path={getPath('upsell')} element={<Upsell />} />
                <Route path={getPath('order')} element={<OrderAndPayment />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
            <ServerErrorModal
                onClick={handleAcceptError}
                isLoading={isTicketProcessing}
            />
            <FamilyConflictModal onClick={handleAcceptConflict} />
            <SeniorConflictModal onClick={handleAcceptConflict} />
            <ConfirmationModal />
        </Page>
    )
}

export default CreatorAccommodation
